import { useMemo, useContext, useEffect } from "react";
import { useFloating, useInteractions, useDismiss, autoUpdate, useClick, flip as flipDropdown, offset, hide as hideMiddleware, } from "@floating-ui/react";
import useTimeout from "@restart/hooks/useTimeout";
import { adjustOffsets, hideWhenTargetHidden, resetFlipIfOverflows, sizeMatchRefWidth, sizeMinRefWidth, } from "../utils/FloatingUtils";
import { MainContainerContext } from "../components/main-container/MainContainerContext";
import { useClosePortalOnScroll, useContextMenu, useEmitCloseEvent, useEscapeOutsideCloses, useFocusPortalChildOnTargetTab, useHideOnPortalTab, useHideOnPortalTabLastElement, useHideOnTargetBlur, useHideOnEscape, useHidePortalWhenOutsideDisabledTarget, useShowOnTargetEnterSpace, useHover, useStopPortalFocusPropagation, } from "./usePortal/interactions";
import useUniqueId from "./useUniqueId";
import { warnOverlappingFocusableElements } from "../utils/DomHelpers";
export const horizontalAlignment = {
    left: "bottom-start",
    right: "bottom-end",
};
export const PORTAL_DELAY_DURATION_MS = 300;
export const TRIGGER_CLICK = "click";
export const TRIGGER_RIGHT_CLICK = "right-click";
export const TRIGGER_HOVER = "hover";
export const TRIGGER_MODAL = "modal";
const usePortalVisibility = ({ visible, onVisibleChange, targetRef, portalOptions: { trapFocus }, }) => {
    const portalTimer = useTimeout();
    const handle = (newState, delay) => {
        portalTimer.clear();
        if (newState !== visible) {
            if (delay > 0) {
                portalTimer.set(() => {
                    onVisibleChange === null || onVisibleChange === void 0 ? void 0 : onVisibleChange(newState);
                    portalTimer.clear();
                }, delay);
            }
            else {
                onVisibleChange === null || onVisibleChange === void 0 ? void 0 : onVisibleChange(newState);
            }
        }
    };
    const show = (delay = PORTAL_DELAY_DURATION_MS) => {
        handle(true, delay);
    };
    const hide = (delay = PORTAL_DELAY_DURATION_MS, returnFocus = false) => {
        handle(false, delay);
        if (!trapFocus && returnFocus) {
            targetRef === null || targetRef === void 0 ? void 0 : targetRef.focus();
        }
    };
    return { show, hide };
};
const withDefaultOptions = (options) => (Object.assign(Object.assign({ position: "absolute" }, options), { portalOptions: Object.assign({ flip: true, minWidthTarget: false, sameWidthTarget: false, skidding: 0, distance: 0, hide: true, shouldHideWhenTargetHidden: true, shouldCloseWhenTargetHidden: false, returnFocusOnHide: true, trapFocus: false, allowTabNavigation: true, dialog: false }, options.portalOptions) }));
const usePortal = (initialOptions) => {
    var _a;
    const options = withDefaultOptions(initialOptions);
    const { onVisibleChange, visible, placement, position, trigger, targetRef, portalRef, eventEmitId, portalOptions: { flip, minWidthTarget, sameWidthTarget, skidding, distance, hide: hideOption, shouldHideWhenTargetHidden, shouldCloseWhenTargetHidden, returnFocusOnHide, allowTabNavigation, trapFocus, dialog, }, } = options;
    useEffect(() => warnOverlappingFocusableElements(targetRef), [targetRef]);
    const uqId = useUniqueId(eventEmitId);
    const evId = eventEmitId || uqId;
    const mainContainerContext = useContext(MainContainerContext);
    const rightBarWidth = (mainContainerContext === null || mainContainerContext === void 0 ? void 0 : mainContainerContext.rightBarWidth) || undefined;
    const middleware = useMemo(() => [
        flip && flipDropdown(),
        flip && resetFlipIfOverflows(),
        offset({ mainAxis: distance, crossAxis: skidding }),
        minWidthTarget && sizeMinRefWidth(),
        sameWidthTarget && sizeMatchRefWidth(true),
        !sameWidthTarget && sizeMatchRefWidth(false),
        hideOption && hideMiddleware(),
        adjustOffsets(),
        shouldHideWhenTargetHidden &&
            hideWhenTargetHidden(() => {
                if (shouldCloseWhenTargetHidden) {
                    onVisibleChange === null || onVisibleChange === void 0 ? void 0 : onVisibleChange(false);
                }
            }, rightBarWidth),
    ], [
        flip,
        minWidthTarget,
        sameWidthTarget,
        skidding,
        distance,
        hideOption,
        shouldHideWhenTargetHidden,
        shouldCloseWhenTargetHidden,
        rightBarWidth,
    ]);
    const { refs, update, placement: adjustedPlacement, floatingStyles, context, } = useFloating({
        open: visible,
        onOpenChange: (newState, e, reason) => {
            newState !== visible && (onVisibleChange === null || onVisibleChange === void 0 ? void 0 : onVisibleChange(newState, e, reason));
        },
        placement,
        strategy: position,
        elements: {
            reference: targetRef,
            floating: portalRef,
        },
        middleware,
        whileElementsMounted: autoUpdate,
    });
    // the domReference is the element that handles the events
    // (see SplitButton, where the element used for positioning is different)
    // https://floating-ui.com/docs/react#changing-the-positioning-reference-while-retaining-events
    const { show, hide } = usePortalVisibility(Object.assign(Object.assign({}, options), { targetRef: (_a = refs.domReference.current) !== null && _a !== void 0 ? _a : refs.reference.current }));
    const { getReferenceProps, getFloatingProps } = useInteractions([
        {
            reference: {
                tabIndex: 0,
            },
            floating: Object.assign({}, (dialog && Object.assign({ role: "dialog", "aria-label": dialog.label }, (trigger === TRIGGER_RIGHT_CLICK && {
                "aria-modal": true,
            })))),
        },
        useClick(context, {
            enabled: trigger && [TRIGGER_CLICK].includes(trigger),
            keyboardHandlers: false,
        }),
        useHover(context, { enabled: trigger === TRIGGER_HOVER, show, hide }),
        // external handlers (document.body, or ancestors)
        useEscapeOutsideCloses(context, { enabled: trigger === TRIGGER_HOVER }),
        useHidePortalWhenOutsideDisabledTarget(context, { enabled: trigger === TRIGGER_HOVER, hide }),
        useClosePortalOnScroll(context, { enabled: trigger === TRIGGER_HOVER, hide }),
        useEmitCloseEvent(context, {
            enabled: trigger === TRIGGER_HOVER || trigger === TRIGGER_RIGHT_CLICK,
            eventEmitId: evId,
            trigger,
        }),
        // target handlers
        useContextMenu(context, { enabled: trigger === TRIGGER_RIGHT_CLICK }),
        useShowOnTargetEnterSpace(context, { enabled: trigger !== TRIGGER_RIGHT_CLICK, show }),
        useFocusPortalChildOnTargetTab(context, { enabled: trigger !== TRIGGER_RIGHT_CLICK, hide }),
        useHideOnTargetBlur(context, { enabled: trigger === TRIGGER_HOVER, hide }),
        // portal handlers
        useHideOnPortalTabLastElement(context, { enabled: Boolean(allowTabNavigation && !trapFocus), hide }),
        useHideOnPortalTab(context, { enabled: !allowTabNavigation, hide, returnFocusOnHide }),
        useStopPortalFocusPropagation(),
        // target and portal handlers
        useHideOnEscape(context, { enabled: trigger !== TRIGGER_RIGHT_CLICK, hide, returnFocusOnHide }),
        useDismiss(context, {
            escapeKey: true,
            bubbles: false,
            referencePressEvent: "mousedown",
        }),
    ]);
    return {
        refs,
        update,
        context,
        placement: adjustedPlacement,
        portalStyles: floatingStyles,
        getTargetProps: (props) => getReferenceProps(Object.assign(Object.assign({}, (visible && { "data-portal-open": true })), props)),
        getPortalProps: getFloatingProps,
        show,
        hide,
    };
};
export default usePortal;
//# sourceMappingURL=usePortal.js.map